import React, {
  useState,
  useEffect,
  useRef,
  useContext,
  useLayoutEffect,
} from 'react';
import {View, AppState, ScrollView, Platform, Pressable, Animated, Easing} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';
import {useNavigation} from '@react-navigation/native';

import UserAvatar from '../../components/elements/UserAvatar';
import Text from '../../components/elements/Text';
import TabHeader from '../../components/elements/TabHeader';
import ViewBloodSugarHistory from '../ViewBloodSugarHistory';

import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../theme';
import {fetchApi} from '../../utils/ApiHelper';
import {
  ACCOUNT_STATUS,
  HTTP_METHODS,
  BLOOD_SUGAR_LEVEL,
  VIEW_DETAILS,
} from '../../utils/URLConstants';
import {DLog, getGreetingText} from '../../utils/HelperMethods';
import {useTimeFormat, formatTime} from '../../hooks/useTimeFormat';
import {accountStatusContext} from '../../contexts/AccountStatusContext';
import {loadingPauseContext} from '../../contexts/LoadingPauseContext';
import {bleSyncContext} from '../../contexts/BLESyncContext';
import {BUTTON_TYPES, KEYS} from '../../utils/Constants';
import {
  getUserProfileData,
  storeUserProfileData,
} from '../../utils/GeneralStorage';
import Button from '../../components/elements/Button';
import SuggestResources from '../SuggestResources';
import {getStoredDevices} from '../../utils/BLEHelperMethods';
import {getData, storeData} from '../../utils/GeneralStorage';
import {shopContext} from '../../contexts/ShopContext';
import {
  hasFeatureEnabled,
  WEB_FEATURE_LEVEL,
} from '../../utils/FeatureConstants';
import CommonStyles from '../../utils/CommonStyles';
import {useOutletContext} from '../../utils/mockedWebLibs/react-router-dom';
import {trackEvent} from '../../utils/Analytics';
import Toast from 'react-native-toast-message';
import {hapticSuccess} from '../../utils/Haptics';
import {BluetoothIcon} from '../../utils/SvgIcons';
import {BLE_SYNC_STATE} from '../../contexts/BLESyncContext';

const HeaderText = ({firstname}) => {
  const {colors} = useTheme();
  return (
    <Text variation="Headline4Regular" color={colors.textSecondary}>
      Welcome back,{' '}
      <Text variation="Headline4SemiBold" color={colors.textPrimary}>
        {` ${firstname}`}
      </Text>
    </Text>
  );
};

const HomePage = ({
  route,
  navigation: navProp,
  ViewBSHistory = false,
  setRightPanelComponent,
}) => {
  // Use navigation hook for drawer access
  const navigation = useNavigation();
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  const [firstname, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [bsRange, setBSRange] = useState({});
  const [showDeviceSyncCTA, setShowDeviceSyncCTA] = React.useState(false);
  const [profileImage, setProfileImage] = useState();
  const [bshRender, setBshRender] = useState(false); //first check if any entry needs syncing
  const newReadingsCount = route?.params?.newReadingsCount;
  const isFutureDateReading = route?.params?.isFutureDateReading;

  const appState = useRef(AppState.currentState);
  const asc = useContext(accountStatusContext);
  const lpc = useContext(loadingPauseContext);
  const ShopC = useContext(shopContext);
  const bleSyncCtx = useContext(bleSyncContext);
  const timeFormat = useTimeFormat();

  // Animation for sync icon
  const syncPulseAnim = useRef(new Animated.Value(1)).current;
  const isSyncing = bleSyncCtx?.isReceivingData || bleSyncCtx?.syncState === BLE_SYNC_STATE.MANUAL_SYNCING;
  const isAutoSendActive = bleSyncCtx?.syncState === BLE_SYNC_STATE.AUTOSEND_LISTENING || bleSyncCtx?.syncState === BLE_SYNC_STATE.AUTOSEND_RECEIVING;

  // Pulse animation when syncing
  useEffect(() => {
    if (isSyncing) {
      const pulse = Animated.loop(
        Animated.sequence([
          Animated.timing(syncPulseAnim, {
            toValue: 1.3,
            duration: 400,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
          Animated.timing(syncPulseAnim, {
            toValue: 1,
            duration: 400,
            easing: Easing.inOut(Easing.ease),
            useNativeDriver: true,
          }),
        ])
      );
      pulse.start();
      return () => pulse.stop();
    } else {
      syncPulseAnim.setValue(1);
    }
  }, [isSyncing, syncPulseAnim]);

  const {setHeaderName} = useOutletContext();

  useLayoutEffect(() => {
    if (setHeaderName) setHeaderName(<HeaderText firstname={firstname} />);
  }, [setHeaderName, firstname]);

  useEffect(() => {
    if (Platform.OS === 'web') {
      getProfile();
      postGlucoseData();
    } else {
      const unsubscribe = navigation.addListener('focus', () => {
        // The screen is focused
        getProfile();
        postGlucoseData();
        // Re-show blood sugar history when returning from ViewBloodSugarHistory
        setBshRender(true);
      });

      // Return the function to unsubscribe from the event so it gets removed on unmount
      return unsubscribe;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount/focus-only profile + glucose sync
  }, [navigation]);

  useEffect(() => {
    getUserProfile();
  }, []);

  // Watch for AutoSend readings - refresh UI when new readings are synced
  useEffect(() => {
    if (bleSyncCtx?.readingsVersion > 0 && Platform.OS !== 'web') {
      DLog('[HomePage] AutoSend reading detected, refreshing blood sugar history');
      // Show toast notification for the new reading
      const reading = bleSyncCtx?.lastReading;
      if (reading) {
        hapticSuccess();
        Toast.show({
          type: 'success',
          text1: `New Reading: ${reading.glucoseValue} mg/dL`,
          text2: 'Synced automatically from your meter',
          visibilityTime: 3000,
        });
      }
      // Force ViewBloodSugarHistory to re-fetch by toggling bshRender
      setBshRender(false);
      setTimeout(() => setBshRender(true), 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- BLE: fire on readingsVersion bump only; lastReading read as current value
  }, [bleSyncCtx?.readingsVersion]);

  useEffect(() => {
    if (newReadingsCount >= 0) {
      if (newReadingsCount > 0) {
        hapticSuccess();
      }
      Toast.show({
        type: 'success',
        text1:
          newReadingsCount > 0
            ? `${newReadingsCount} New Readings Successfully Synced!`
            : 'No New Readings, You Are Up to Date!',
        onHide: () => {
          if (isFutureDateReading) {
            Toast.show({
              type: 'error',
              text1: `Your Diathrive Meter is set to a future date. Please update your settings to prevent errors when syncing.`,
            });
          }
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync toast intentionally keyed to route params only
  }, [route?.params]);

  const getUserProfile = () => {
    fetchApi(HTTP_METHODS.GET, VIEW_DETAILS)
      .then(res => res.data)
      .then(async response => {
        const profile = response.data.data;
        const localProfile = await getUserProfileData();
        storeUserProfileData({...localProfile, ...profile});
        setProfileImage(profile.signedImageUrl);
      })
      .catch(error => {
        // Dlog(error);
      });
  };

  const postGlucoseData = async () => {
    lpc.setVisible(true);
    let isNewReadingsSynced = false;
    let devices = await getStoredDevices();
    const userId = await getData(KEYS.userId);
    //iterate in devices
    for (let i = 0; i < devices?.length; i++) {
      // Use device.id as primary storage key, with fallback to device.name for backwards compatibility
      const storageKey = devices[i].id;
      let existingDataById = await getData(storageKey);
      let existingDataByName = await getData(devices[i].name);

      // Merge data from both keys (backwards compatibility migration)
      let existingData = {};
      if (existingDataByName) {
        existingData = {...existingData, ...JSON.parse(existingDataByName)};
      }
      if (existingDataById) {
        existingData = {...existingData, ...JSON.parse(existingDataById)};
      }

      // DLog(existingData);
      for (let key in existingData) {
        if (
          existingData[key].userId === userId &&
          existingData[key].isSynced == 0
        ) {
          //un synced data found, sync it
          setBshRender(false);
          const params = {
            measureAt: existingData[key].dateTimeOfReading,
            bloodSugarCount: existingData[key].glucoseValue,
          };
          // DLog(params);
          //lpc.setVisible(true);
          try {
            let res = await fetchApi(
              HTTP_METHODS.POST,
              BLOOD_SUGAR_LEVEL,
              params,
            );
            if (res.status == 200) {
              isNewReadingsSynced = true;
              existingData[key].isSynced = 1;
            }
          } catch (e) {
            // lpc.setVisible(false);
            // Dlog(e);
            if (e && e.message == 'Duplicate data.') {
              //entry is already synced
              existingData[key].isSynced = 1;
            }
          }
        }
      }

      // PHI boundary: no device ids/serials or reading values to third-party analytics
      if (isNewReadingsSynced) trackEvent('Sync New Readings');

      //lpc.setVisible(false);
      // Store under device.id (new standard key)
      await storeData(storageKey, JSON.stringify(existingData));
    }

    lpc.setVisible(false);
    setBshRender(true);
  };

  const getProfile = async () => {
    const profile = await getUserProfileData();
    let lastTime = await getData(KEYS.lastSyncedTime);
    setFirstName(profile.firstname);
    setLastName(profile.lastname);
    setBSRange({
      min: profile?.patientCondition?.[0]?.minValue,
      max: profile?.patientCondition?.[0]?.maxValue,
      lastSyncedAt: lastTime,
    });
    if (profile.signedImageUrl) {
      setProfileImage(profile.signedImageUrl);
    }
  };

  useEffect(() => {
    asc.getUserAccountStatus();
    const subscription = AppState.addEventListener('change', nextAppState => {
      if (
        appState.current.match(/inactive|background/) &&
        nextAppState === 'active'
      ) {
        asc.getUserAccountStatus();
      }

      appState.current = nextAppState;
    });

    return () => {
      subscription.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only status fetch + AppState listener; asc identity is unstable
  }, []);

  const getHomeHeadeerText = () => {
    return (
      <Text variation="Headline4Regular" color={colors.textDisabled}>
        {getGreetingText()},{' '}
        <Text variation="Headline4Bold" color={colors.textSecondary}>
          {firstname}
        </Text>
      </Text>
    );
  };

  // Navigate to My Account screen when avatar is pressed
  const openMyAccount = () => {
    navigation.navigate('MyAccountScreen');
  };

  const renderHeaderRightItems = () => {
    return (
      Platform.OS !== 'web' && (
        <View style={styles.flexRow}>
          {/* Device sync icon - tappable, animates when syncing */}
          <Pressable
            onPress={() => navigation.navigate('ConnectedDevices')}
            style={styles.deviceIconWrapper}>
            <Animated.View
              style={[
                styles.deviceIconContainer,
                isAutoSendActive && styles.deviceIconActive,
                isSyncing && styles.deviceIconSyncing,
                {transform: [{scale: syncPulseAnim}]},
              ]}>
              <BluetoothIcon
                width={18}
                height={18}
                // On an accent-filled circle the glyph must be textOnAccent — accentStrong
                // === accent in dark (both brand cyan), which dissolved the icon into
                // a solid disc (iOS repro: meter paired -> AutoSend active).
                fill={isSyncing || isAutoSendActive ? colors.textOnAccent : colors.textSecondary}
              />
            </Animated.View>
          </Pressable>
          <UserAvatar
            name={firstname + ' ' + lastName}
            source={profileImage}
            size={32}
            radius={8}
            onPress={openMyAccount}
          />
        </View>
      )
    );
  };

  const getLastSyncedAt = () => {
    //if last sync was today show hours else show full date
    if (bsRange.lastSyncedAt) {
      let lastSyncedAt = new Date(bsRange.lastSyncedAt);
      let today = new Date();
      //if today = last sycned date
      if (
        today.getDate() == lastSyncedAt.getDate() &&
        today.getMonth() == lastSyncedAt.getMonth() &&
        today.getFullYear() == lastSyncedAt.getFullYear()
      ) {
        return 'Last sync at ' + formatTime(bsRange.lastSyncedAt, timeFormat);
      } else {
        return 'Last sync on' + bsRange.lastSyncedAt?.substring(4, 10);
      }
    } else return null;
  };

  if (ViewBSHistory) {
    return (
      <ViewBloodSugarHistory
        hypo={bsRange.min}
        hyper={bsRange.max}
        setRightPanelComponent={setRightPanelComponent}
      />
    );
  }

  return (
    <SafeAreaView style={styles.mainView()}>
      {Platform.OS !== 'web' && (
        <TabHeader
          title={getHomeHeadeerText()}
          renderRight={renderHeaderRightItems}
        />
      )}
      <ScrollView
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.homeScrollContent}>
        {(Platform.OS !== 'web' ||
          hasFeatureEnabled(WEB_FEATURE_LEVEL.bloodSugarReading)) &&
          bshRender && (
            <View style={Platform.OS === 'web' && styles.webBSHistory}>
              <ViewBloodSugarHistory
                homeMode={true}
                setShowDeviceSyncCTA={setShowDeviceSyncCTA}
                hypo={bsRange.min}
                hyper={bsRange.max}
                lastSyncedAt={getLastSyncedAt()}
              />
            </View>
          )}

        <View style={styles.buttonContainerStyle}>
          {Platform.OS !== 'web' && (
            <Button
              title="My Meter"
              titleColor={colors.textOnAccent}
              buttonType={BUTTON_TYPES.Filled}
              onPress={() => {
                if (Platform.OS !== 'web') {
                  navigation.navigate('ConnectedDevices');
                }
              }}
              style={styles.flex1Width292}
            />
          )}
        </View>

        {(Platform.OS !== 'web' ||
          hasFeatureEnabled(WEB_FEATURE_LEVEL.bloodSugarReading)) && (
          <View style={styles.buttonContainerStyle}>
            <Button
              title="View Blood Sugar History"
              titleColor={colors.link}
              buttonType={BUTTON_TYPES.Plain}
              style={[styles.buttonStyle, styles.flex1Width292]}
              onPress={() => {
                if (Platform.OS !== 'web') {
                  navigation.navigate('ViewBloodSugarHistory', {
                    hypo: bsRange.min,
                    hyper: bsRange.max,
                  });
                  setBshRender(false);
                } else navigation('/blood-sugar-readings');
              }}
            />
          </View>
        )}
        <View
          style={Platform.OS === 'web' ? CommonStyles.m_32 : styles.marginSet}>
          <SuggestResources title={'From Us To You'} isSeeAll={true} />
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

export default HomePage;
